import React, { forwardRef, Suspense, useRef } from "react"
import { useParams } from "react-router-dom"
import { useNavigate } from "react-router"
import { FormikProps } from "formik"
import { MERCHANT_ADMIN_ROUTE, MERCHANT_ADMIN_SETTINGS_DELIVERY_LOCATIONS_ROUTE } from "../../../../../constants/routes"
import { CreateUpdateDeliveryLocationForm } from "../components/CreateUpdateDeliveryLocationForm"
import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { CreateOrEditDeliveryLocationFormValues } from "../deliveryLocationsTypes"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { queryKeyFor, useApiQuery } from "../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../core/api/errorTypes"
import { prepareInitialValues } from "../helpers/prepareInitialValues"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import {
    deliveryLocationDetails,
    DeliveryLocationDetailsResponse,
    DeliveryLocationsQuery,
    updateDeliveryLocation,
} from "../deliveryLocationsApi"
import { useDateTime } from "../../../../localization/useDateTime"

export function EditDeliveryLocation() {
    const formRef = useRef<FormikProps<CreateOrEditDeliveryLocationFormValues>>(null)

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    return (
        <>
            <LocalizedStrict id="merchant-delivery-location-edit-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.MERCHANT_ADMIN_DELIVERY_LOCATION_UPDATE}
                    title="Edit delivery location"
                    rightElement={
                        <LocalizedStrict id="merchant-delivery-locations-save-button">
                            <StandardButton onClick={handleSaveClick}>Save</StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-settings-header">
                            <Breadcrumb>Settings</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-delivery-locations">
                            <BreadcrumbLink to={MERCHANT_ADMIN_SETTINGS_DELIVERY_LOCATIONS_ROUTE}>
                                Delivery locations
                            </BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-delivery-location-edit-breadcrumb">
                            <Breadcrumb>Edit delivery location</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <DeliveryLocation ref={formRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}

function transformData({ deliveryLocation }: DeliveryLocationDetailsResponse) {
    return deliveryLocation
}

const DeliveryLocation = forwardRef<FormikProps<CreateOrEditDeliveryLocationFormValues>>((props, ref) => {
    const navigate = useNavigate()
    const { deliveryLocationId } = useParams()
    const dateTimeFactory = useDateTime()

    const { data: deliveryLocationResult } = useApiQuery(deliveryLocationDetails, {
        queryName: DeliveryLocationsQuery.DETAILS,
        dependencies: {
            deliveryLocationId: deliveryLocationId || "",
        },
        enabled: Boolean(deliveryLocationId),
        suspense: true,
        useErrorBoundary: true,
        select: transformData,
    })

    const { mutate: callUpdateDeliveryLocation } = useApiMutation(updateDeliveryLocation, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(params, _, context) {
            await context.queryClient.invalidateQueries({ queryKey: [DeliveryLocationsQuery.LIST] })
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(DeliveryLocationsQuery.DETAILS, {
                    deliveryLocationId: params.deliveryLocationId,
                }),
            })
            navigate(MERCHANT_ADMIN_SETTINGS_DELIVERY_LOCATIONS_ROUTE)
        },
    })

    return (
        <CreateUpdateDeliveryLocationForm
            ref={ref}
            initialValues={prepareInitialValues(dateTimeFactory, deliveryLocationResult)}
            onSubmit={(data) => {
                deliveryLocationId && callUpdateDeliveryLocation({ deliveryLocation: data, deliveryLocationId })
            }}
        />
    )
})
